import React, { useEffect, useState } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import BackOfficeMenu from "../components/BackOfficeMenu";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { usePermissions } from "../../utils/usePermissions";
import "./DashboardEquipe.css";

const API_URL = import.meta.env.VITE_API_URL;

const COLORS = ['#1e3a5f', '#ffd700', '#ff8c00', '#00C49F', '#ff4d4d', '#7c3aed', '#db2777'];

const DashboardEquipe = ({ onNavigate, onLogout, currentPage }) => {
    const { hasPermission, typeUser } = usePermissions();
    const [equipes, setEquipes] = useState([]);
    const [statsGlobales, setStatsGlobales] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (typeUser && typeUser !== "admin" && typeUser !== "moderateur") {
            onNavigate("profile");
        }
    }, [onNavigate, typeUser]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const [equipesRes, statsRes] = await Promise.all([
                axios.get(`${API_URL}/equipes-installation`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/equipes-installation/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            setEquipes(equipesRes.data);
            setStatsGlobales(statsRes.data);
        } catch (error) {
            console.error('Erreur chargement dashboard équipes:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="dashboard-content">
                    <LoadingSpinner message="Calcul des performances..." />
                </div>
            </div>
        );
    }

    const chartData = equipes.map(eq => ({
        name: eq.chefEquipe?.nom || 'Sans nom',
        clients: eq.totalClients || 0,
        kwc: eq.totalKWc || 0
    })).sort((a, b) => b.kwc - a.kwc);

    return (
        <div className="dashboard-container">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div>
                        <h1>Performance des Équipes</h1>
                        <p>Analyse de productivité et impact installation</p>
                    </div>
                    <button className="refresh-btn" onClick={fetchData}>Actualiser</button>
                </div>

                {/* Stats Globales */}
                <div className="stats-cards">
                    <div className="stat-card blue">
                        <div className="stat-value">{statsGlobales?.totalEquipes || 0}</div>
                        <div className="stat-label">Équipes Actives</div>
                    </div>
                    <div className="stat-card green">
                        <div className="stat-value">{statsGlobales?.totalClientsInstalles || 0}</div>
                        <div className="stat-label">Installations Réalisées</div>
                    </div>
                    <div className="stat-card orange">
                        <div className="stat-value">{statsGlobales?.totalKWcInstalles?.toFixed(1) || 0}</div>
                        <div className="stat-label">Total kWc Installés</div>
                    </div>
                </div>

                <div className="charts-grid-equipe">
                    {/* Graphique kWc par équipe */}
                    <div className="chart-card-full">
                        <h3>Puissance Totale Installée par Équipe (kWc)</h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" />
                                <YAxis label={{ value: 'kWc', angle: -90, position: 'insideLeft' }} />
                                <Tooltip formatter={(value) => [`${value} kWc`, 'Puissance']} />
                                <Bar dataKey="kwc" radius={[8, 8, 0, 0]}>
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="performance-table-container">
                        <h3>Classement Productivité</h3>
                        <div className="premium-table-wrapper">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>Équipe (Chef)</th>
                                        <th>Type</th>
                                        <th>Installations</th>
                                        <th>Total kWc</th>
                                        <th>Ratio moyen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {equipes.sort((a,b) => b.totalKWc - a.totalKWc).map((eq, i) => (
                                        <tr key={eq._id}>
                                            <td>
                                                <div className="chef-info">
                                                    <span className="rank">{i+1}</span>
                                                    <strong>{eq.chefEquipe?.nom} {eq.chefEquipe?.prenom}</strong>
                                                </div>
                                            </td>
                                            <td><span className="badge-type">{eq.typeEquipe}</span></td>
                                            <td className="center">{eq.totalClients}</td>
                                            <td className="center bold">{eq.totalKWc?.toFixed(2)}</td>
                                            <td className="center">
                                                {eq.totalClients > 0 ? (eq.totalKWc / eq.totalClients).toFixed(2) : 0} kWc/inst
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardEquipe;
